import { type MouseEvent, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router';
import { Badge, Box, Logo, TopNav, type TopNavItem } from '@tempmee/react';
import { OutlineChevronDownIcon, OutlineLocationMarkerIcon } from '@tempmee/react-icons';
import { NAVBAR_HEIGHT, NAVBAR_Z_INDEX } from '~/constants/layout';
import { useAppSelector } from '~/hooks/useAppSelector';
import { useParentOffice } from '~/hooks/useParentOffice';
import { hasChildOffices } from '~/utils/OfficeUtils';
import { logOut, selectOffice, showMessage } from '../../actions';
import history from '../../browserHistory';
import { fireMenuBarAccountClicked, fireMenuBarInboxClicked } from '~/posthogs/events';
import { useInboxCount } from '../Inbox/hooks/useInboxCount';
import { AccountSplitWeb } from '~/constants/ff.posthog';
import { useFeatureFlag } from '~/hooks/useFeatureFlag';
import { routes } from '~/routes';
import { LocationMenu, LocationMenuTriggerLabel } from './LocationMenu';

interface ITopNavBarProps {
  hasOfficeSelected?: boolean;
}

/**
 * Pins the navbar to the top of the viewport. The v3 TopNav has no `position`
 * prop (the old DS Navbar took `position="fixed"`), so the header is floated in
 * a fixed layer over an in-flow spacer div that reserves its height. The sticky
 * SideBar (top: NAVBAR_HEIGHT) depends on the header staying put here.
 */
const fixedLayerStyle = {
  position: 'fixed' as const,
  top: 0,
  left: 0,
  right: 0,
  zIndex: NAVBAR_Z_INDEX,
};

export default function TopNavBar({ hasOfficeSelected = true }: ITopNavBarProps) {
  const location = useLocation();
  const dispatch = useDispatch();
  const { totalInbox } = useInboxCount();
  // With the account split on, "Account" becomes the person's own "My profile".
  const isAccountSplitOn = useFeatureFlag(AccountSplitWeb);
  const accountRoute = isAccountSplitOn ? routes.myProfile : routes.account;

  // GOTU-8264: with the account split on, a multi-location account changes location
  // from the top bar instead of the sidebar card.
  const childOffices = useAppSelector((state) => state.user.childOffices);
  const selectedOfficeId = useAppSelector((state) => state.user.selectedOfficeId);
  const selectedChildOffice = useAppSelector((state) => state.user.selectedChildOffice);
  const { isParentOffice } = useParentOffice();
  const showLocationMenu = isAccountSplitOn && hasChildOffices(childOffices);
  const [locationMenuAnchor, setLocationMenuAnchor] = useState<HTMLElement | null>(null);

  // The parent office is the "All locations" view. A location's name comes from the
  // selected-office record when it is current (a Location settings save refreshes it),
  // else from the account's location list.
  const selectedLocationName = (() => {
    if (!selectedOfficeId || isParentOffice) return 'All locations';
    if (selectedChildOffice?.id === selectedOfficeId && selectedChildOffice.office_name) {
      return selectedChildOffice.office_name;
    }
    return (
      childOffices.find((office) => office.id === selectedOfficeId)?.office_name ?? 'All locations'
    );
  })();

  const toggleLocationMenu = (event: MouseEvent<HTMLElement>) => {
    const trigger = event.currentTarget;
    setLocationMenuAnchor((current) => (current ? null : trigger));
  };

  const closeLocationMenu = () => setLocationMenuAnchor(null);

  // Re-picking the selected location only closes the menu: there is nothing to switch to.
  const handleLocationSelect = (officeId: string) => {
    closeLocationMenu();
    if (officeId !== selectedOfficeId) {
      dispatch(selectOffice({ officeId }));
    }
  };

  const goToInbox = () => {
    if (location.pathname === '/registration') {
      dispatch(
        showMessage({
          title: 'Error',
          message: 'Please finish your registration to access the rest of the platform.',
          isError: true,
        }),
      );
    } else {
      fireMenuBarInboxClicked();
      history.push('/dashboard/inbox');
    }
  };

  const goToAccount = () => {
    fireMenuBarAccountClicked();
    history.push(accountRoute);
  };

  const handleLogout = () => {
    sessionStorage.clear();
    dispatch(logOut());
  };

  // Map the active nav item from the current route (controlled `value`).
  const activeValue = (() => {
    if (location.pathname === '/dashboard/inbox') return 'inbox';
    if (location.pathname === accountRoute) return 'account';
    return undefined;
  })();

  const handleNavChange = (value: string) => {
    switch (value) {
      case 'inbox':
        goToInbox();
        break;
      case 'account':
        goToAccount();
        break;
      case 'logout':
        handleLogout();
        break;
      case 'location':
        // The location item opens its menu from its own onClick; it is not a destination.
        break;
    }
  };

  const isRegistration = location.pathname.includes('registration');

  const items: TopNavItem[] = (() => {
    if (isRegistration) return [];
    if (!hasOfficeSelected) {
      return [{ value: 'logout', label: 'Log Out', 'data-cy': 'top-nav-logout-menu' }];
    }
    return [
      {
        value: 'inbox',
        label: 'Inbox',
        'data-cy': 'top-nav-inbox-menu',
        badge: totalInbox > 0 ? <Badge severity="flagged" count={totalInbox} /> : undefined,
      },
      ...(showLocationMenu
        ? [
            {
              value: 'location',
              label: <LocationMenuTriggerLabel name={selectedLocationName} />,
              icon: <OutlineLocationMarkerIcon />,
              badge: <OutlineChevronDownIcon />,
              'data-cy': 'top-nav-location-menu',
              'aria-haspopup': 'dialog' as const,
              'aria-expanded': locationMenuAnchor !== null,
              onClick: toggleLocationMenu,
            },
          ]
        : []),
      {
        value: 'account',
        label: isAccountSplitOn ? 'My profile' : 'Account',
        'data-cy': 'top-nav-account-menu',
      },
    ];
  })();

  return (
    <Box height={`${NAVBAR_HEIGHT}px`}>
      <Box data-testid="top-nav-fixed-layer" style={fixedLayerStyle}>
        <TopNav
          color="dark"
          value={activeValue ?? ''}
          onChange={handleNavChange}
          items={items}
          logo={
            <Logo
              tone="light"
              mark="full"
              height={44}
              style={{ cursor: 'pointer' }}
              onClick={() => history.push('/')}
            />
          }
        />
        {showLocationMenu && (
          <LocationMenu
            anchorEl={locationMenuAnchor}
            onClose={closeLocationMenu}
            onSelect={handleLocationSelect}
          />
        )}
      </Box>
    </Box>
  );
}
